import type { ICreateLocationCmd } from "@/data/commands/location/CreateLocationCmd";
import type { ICreateLocationPaymentMethod } from "@/data/commands/location/CreateLocationPaymentMethod";
import type { ILocationExportCmd } from "@/data/commands/location/LocationExportCmd";
import type { IUpdateLocationApiSettingsCmd } from "@/data/commands/location/UpdateLocationApiSettingsCmd";
import type { IUpdateLocationDesignCmd } from "@/data/commands/location/UpdateLocationDesignCmd";
import type { IUpdateLocationGeneralSettingsCmd } from "@/data/commands/location/UpdateLocationGeneralSettingsCmd";
import type { IUpdateLocationPrivacySettingsCmd } from "@/data/commands/location/UpdateLocationPrivacySettingsCmd";
import type {
    IUpdateLocationPspSettingsCmd,
} from "@/data/commands/location/UpdateLocationPspSettingsCmd";
import { ServiceType } from "@/data/model/Enums";
import type {
    IAddress,
    IAllocatableLocation,
    IApiSettings,
    IDesign,
    IGeneralSettings,
    ILocation,
    ILocationDetail,
    ILocationPrivacySettings, ILocationRelation,
    ITwilioAccount,
} from "@/data/model/Location";
import type { ILocationPaymentMethod } from "@/data/model/LocationPaymentMethod";
import type { IPermalink } from "@/data/model/Permalink";
import type { IConfigSchemaRow, ILinkableEntity } from "@/data/model/Shared";
import type { IIdentity, IUserAccessGrant } from "@/data/model/User";
import type { IPaginatedQry } from "@/data/queries/PaginatedQry";
import type { IAllocatableLocationsQry } from "@/data/queries/location/AllocatableLocationsQry";
import type { ILocationsQry } from "@/data/queries/location/LocationsQry";
import { DateTime } from "@/data/util/DateTime";

import {
    BaseServiceDao,
    type IDataSource,
    type IEditableDataSource,
    type IMultiEntityLinkDataSource,
    type IPaginatedResult,
    parseCollection,
    parsePaginatedResult,
} from "./BaseServiceDao";
import { PlanningDao } from "./PlanningDao";
import { SystemDao } from "./SystemDao";
import { TableDao } from "./TableDao";
import { UserDao } from "./UserDao";
import type { IUpdateChildLocationsCmd } from "@/data/commands/location/IUpdateChildLocationsCmd";

export class LocationDao
    extends BaseServiceDao
    implements IEditableDataSource<ILocation, ILocationDetail>, IMultiEntityLinkDataSource
{
    public static className = "LocationDao";

    constructor() {
        super("location");
    }

    public async getSingle(id: string): Promise<ILocationDetail> {
        return this.get(id, undefined, LocationDao.parseLocationDetail);
    }

    public async getData(qry: ILocationsQry): Promise<IPaginatedResult<ILocation>> {
        return this.get("", qry, (l) => parsePaginatedResult(l, LocationDao.parseLocation));
    }

    public async create(cmd: ICreateLocationCmd): Promise<ILocationDetail> {
        return this.post("", cmd, LocationDao.parseLocationDetail);
    }

    public async update(id: string, cmd: ICreateLocationCmd): Promise<ILocationDetail> {
        return this.put(id, cmd, LocationDao.parseLocationDetail);
    }

    public async remove(id: string): Promise<void> {
        return this.delete(id);
    }

    public async suspend(id: string): Promise<void> {
        return this.post(id + "/suspend", {});
    }

    public async reactivate(id: string): Promise<void> {
        return this.post(id + "/reactivate", {});
    }

    public async getChildLocations(id: string): Promise<Array<ILocationRelation>> {
        return this.get(id + "/child-locations", undefined, (c) => parseCollection(c, LocationDao.parseLocationRelation));
    }
    public async getLocationUsers(id: string): Promise<IPaginatedResult<IIdentity>> {
        return this.get(id + "/users", undefined, (c) => parsePaginatedResult(c, UserDao.parseIdentity));
    }

    public async getLocationAvailablePaymentMethods(id: string): Promise<Array<ILocationPaymentMethod>> {
        return this.get(id + "/payment-methods", undefined, (c) =>
            parseCollection(c, LocationDao.parseLocationPaymentMethod)
        );
    }

    public async createLocationPaymentMethod(
        locationId: string,
        cmd: ICreateLocationPaymentMethod
    ): Promise<ILocationPaymentMethod> {
        return this.post(locationId + "/payment-methods", cmd, LocationDao.parseLocationPaymentMethod, true);
    }

    public async updateLocationPaymentMethod(
        locationId: string,
        id: string,
        cmd: ICreateLocationPaymentMethod
    ): Promise<ILocationPaymentMethod> {
        return this.put(locationId + "/payment-methods/" + id, cmd, LocationDao.parseLocationPaymentMethod, true);
    }

    public async removeLocationPaymentMethod(locationId: string, id: string): Promise<void> {
        return this.delete(locationId + "/payment-methods/" + id);
    }

    public async getAllocatable(qry: IAllocatableLocationsQry): Promise<IPaginatedResult<IAllocatableLocation>> {
        return this.get("allocatable", qry, (c) => parsePaginatedResult(c, (c) => c));
    }

    public async setDesign(id: string, cmd: IUpdateLocationDesignCmd): Promise<ILocationDetail> {
        return this.put(id + "/design", cmd, LocationDao.parseLocationDetail, true);
    }

    public async setGeneralSettings(id: string, cmd: IUpdateLocationGeneralSettingsCmd): Promise<ILocationDetail> {
        return this.put(id + "/general", cmd, LocationDao.parseLocationDetail);
    }

    public async setApiAccess(id: string, cmd: IUpdateLocationApiSettingsCmd): Promise<ILocationDetail> {
        return this.put(id + "/apiaccess", cmd, LocationDao.parseLocationDetail);
    }

    public async setPsp(id: string, cmd: IUpdateLocationPspSettingsCmd): Promise<ILocationDetail> {
        return this.put(id + "/psp", cmd, LocationDao.parseLocationDetail);
    }

    public async setChildLocations(id: string, cmd: IUpdateChildLocationsCmd): Promise<ILocationDetail> {
        return this.put(id + "/child-locations", cmd, LocationDao.parseLocationDetail);
    }

    public async setPrivacySettings(id: string, cmd: IUpdateLocationPrivacySettingsCmd): Promise<ILocationDetail> {
        return this.put(id + "/privacy", cmd, LocationDao.parseLocationDetail);
    }

    public async getServiceAvailability(id: string): Promise<Array<ServiceType>> {
        return this.get(id + "/service/availability", undefined, (c) => parseCollection(c, parseInt));
    }

    public async getServiceConfigSchema(locationId: string, service: ServiceType): Promise<Array<IConfigSchemaRow>> {
        return this.get(locationId + "/service/" + ServiceType[service].toLowerCase() + "/schema", undefined, (f) =>
            parseCollection(f, SystemDao.parseSchemaConfigRow)
        );
    }

    public async activateTwilioAccount(id: string): Promise<ITwilioAccount> {
        return this.post(id + "/twilio", {}, LocationDao.parseTwilioAccount);
    }

    public async disableTwilioAccount(id: string): Promise<void> {
        return this.delete(id + "/twilio", {});
    }

    //permalinks
    public async getPermalink(locationId: string): Promise<IPermalink> {
        return this.get(locationId + "/permalink/", undefined, TableDao.parsePermalink);
    }

    public async createPermalink(locationId: string): Promise<IPermalink> {
        return this.post(locationId + "/permalink/", {}, TableDao.parsePermalink);
    }

    public async removePermalink(locationId: string, permalinkId: string): Promise<void> {
        return this.delete(locationId + "/permalink/" + permalinkId, {});
    }

    public async downloadPermalinkQrCode(locationId: string) {
        location.href = this.basePath + "/" + locationId + "/permalink/qrcode?c=" + new Date().getTime();
    }

    public exportLocations(data: ILocationExportCmd) {
        return this.postDownloadFile("export", data);
    }

    //acl
    public async getGrants(
        locationId: string,
        qry: IPaginatedQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IUserAccessGrant>> {
        return this.get(
            locationId + "/acl/",
            qry,
            (f) => parsePaginatedResult(f, UserDao.parseUserAccessGrant),
            abortSignal
        );
    }

    public async search(excludeIds: Array<string>, term?: string): Promise<Array<ILinkableEntity>> {
        return this.get("search", { Exclude: excludeIds, Term: term }, (f) =>
            parseCollection(f, (h) => {
                return {
                    id: h.id,
                    name: h.name,
                };
            })
        );
    }

    public async searchChildLocations(locationId: string, excludeIds: Array<string>, term?: string): Promise<Array<ILinkableEntity>> {
        return this.get(`${locationId}/search-child-locations`, { Exclude: excludeIds, Term: term }, (f) =>
            parseCollection(f, (h) => {
                return {
                    id: h.id,
                    name: h.name,
                };
            })
        );
    }
    

    public aclDataSource(user: string): IDataSource<IUserAccessGrant> {
        return {
            getData: (filter, abortSignal) => this.getGrants(user, filter, abortSignal),
        };
    }

    public static parseLocation(literal: any): ILocation {
        return {
            id: literal.id,
            name: literal.name,
            internalName: literal.internalName == null ? undefined : literal.internalName,
            state: parseInt(literal.state),
            language: parseInt(literal.language),
            created: DateTime.parseIsoDate(literal.created),
            childLocations: parseCollection(literal.childLocations, LocationDao.parseLocationRelation),
        };
    }

    public static parseLocationRelation(literal: any): ILocationRelation {
        return {
            id: literal.id,
            childLocationId: literal.childLocationId,
            parentLocationId: literal.parentLocationId,
            childLocationInternalName: literal.childLocationInternalName == null ? undefined : literal.childLocationInternalName,
            childLocationName: literal.childLocationName == null ? undefined : literal.childLocationName,
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
        };
    }

    public static parseLocationDetail(literal: any): ILocationDetail {
        return {
            ...LocationDao.parseLocation(literal),
            slug: literal.slug,
            billingAddress: LocationDao.parseAddress(literal.billingAddress),
            address: literal.address != null ? LocationDao.parseAddress(literal.address) : undefined,
            generalSettings: LocationDao.parseGeneralSettings(literal.generalSettings),
            apiSettings: literal.apiSettings != null ? LocationDao.parseApiSettings(literal.apiSettings) : undefined,
            privacySettings:
                literal.privacySettings != null ? LocationDao.parsePrivacySettings(literal.privacySettings) : undefined,
            twilioAccount:
                literal.twilioAccount != null ? LocationDao.parseTwilioAccount(literal.twilioAccount) : undefined,
            company: literal.company,
            email: literal.email == null ? undefined : literal.email,
            phoneNumber: literal.phoneNumber == null ? undefined : literal.phoneNumber,
            website: literal.website == null ? undefined : literal.website,
            vatNumber: literal.vatNumber == null ? undefined : literal.vatNumber,
            design: literal.design == null ? {} : LocationDao.parseDesign(literal.design),
            pspSettings: {
                locationPaymentMethods: parseCollection(
                    literal.locationPaymentMethods,
                    LocationDao.parseLocationPaymentMethod
                ),
            },
            googleMapsLocationLink: literal.googleMapsLocationLink == null ? undefined : literal.googleMapsLocationLink,
        };
    }

    public static parseGeneralSettings(literal: any): IGeneralSettings {
        if (literal == null) return {};

        return {
            stockReceiver: literal.stockReceiver == null ? undefined : literal.stockReceiver,
            dailyOrderIncrementStart:
                literal.dailyOrderIncrementStart == null ? undefined : parseInt(literal.dailyOrderIncrementStart),
            shiftStart: literal.shiftStart == null ? undefined : literal.shiftStart,
        };
    }

    public static parseDesign(literal: any): IDesign {
        return {
            color: literal.color == null ? undefined : literal.color,
            logoDarkPath: literal.logoDarkPath == null ? undefined : literal.logoDarkPath,
            logoLightPath: literal.logoLightPath == null ? undefined : literal.logoLightPath,
            colorPrimary: literal.colorPrimary == null ? undefined : literal.colorPrimary,
        };
    }

    public static parseApiSettings(literal: any): IApiSettings {
        return {
            isActive: literal.isActive,
            isDebugMode: literal.isDebugMode,
            user: literal.user,
            apiVersionId: literal.apiVersionId,
        };
    }

    public static parsePrivacySettings(literal: any): ILocationPrivacySettings {
        return {
            isActive: literal.isActive,
            retention: literal.retention == null ? undefined : parseInt(literal.retention),
            pushToBernApi: literal.pushToBernApi == null ? undefined : literal.pushToBernApi,
            visibleEndUserProperties: parseInt(literal.visibleEndUserProperties),
        };
    }

    public static parseAddress(literal: any): IAddress {
        return {
            city: literal.city == null ? undefined : literal.city,
            country: literal.country == null ? undefined : literal.country,
            street: literal.street == null ? undefined : literal.street,
            zip: literal.zip == null ? undefined : literal.zip,
        };
    }

    public static parseTwilioAccount(literal: any): ITwilioAccount {
        return {
            sid: literal.sid,
            serviceId: literal.serviceId,
        };
    }

    public static parseLocationPaymentMethod(literal: any): ILocationPaymentMethod {
        return {
            id: literal.id == null ? undefined : literal.id,
            locationId: literal.locationId,
            provider: literal.provider == null ? undefined : parseInt(literal.provider),
            externalPaymentId: literal.externalPaymentId == null ? undefined : literal.externalPaymentId,
            merchantId: literal.merchantId == null ? undefined : literal.merchantId,
            predefinedPaymentMethod:
                literal.predefinedPaymentMethod == null
                    ? undefined
                    : PlanningDao.parsePredefinedPaymentMethod(literal.predefinedPaymentMethod),
            isEnabled: literal.isEnabled,
            order: literal.order == null ? undefined : parseInt(literal.order),
            isCustom: literal.isCustom,
            mode: literal.mode,
            name: literal.name == null ? undefined : literal.name,
            logoPath: literal.logoPath == null ? undefined : literal.logoPath,
            isCardScheme: literal.isCardScheme,
            isPaymentDevice: literal.isPaymentDevice,
            isProviderEditable: literal.isProviderEditable,
            useCollectiveCardIcon: literal.useCollectiveCardIcon,
            isEditEnabled: literal.isEditEnabled,
            allowTipOnlyOnDevice: literal.allowTipOnlyOnDevice,
            revenueEffective: literal.revenueEffective,
        };
    }
}
